import React, { useState } from "react";
import { Hotel, MapPin, Calendar, Search, Users, ArrowRight, CheckCircle2, Star, Wifi, Utensils, Waves, Sparkles, BedDouble } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { HOTELS, POPULAR_HOTEL_CITIES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const HotelSearch = () => {
  const { toast } = useToast();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const [q, setQ] = useState({
    city: "",
    checkIn: today.toISOString().slice(0, 10),
    checkOut: tomorrow.toISOString().slice(0, 10),
    rooms: 1,
    guests: 2,
  });
  const [results, setResults] = useState(HOTELS);
  const [selected, setSelected] = useState(null);
  const [guest, setGuest] = useState({ name: "", phone: "", email: "" });
  const [confirmed, setConfirmed] = useState(null);

  const search = (e) => {
    e.preventDefault();
    if (!q.city) {
      toast({ title: "Please enter a city", description: "Where would you like to stay?" });
      return;
    }
    const filtered = HOTELS.filter((h) =>
      h.city.toLowerCase().includes(q.city.toLowerCase())
    );
    setResults(filtered.length ? filtered : HOTELS);
    toast({
      title: `${filtered.length || HOTELS.length} hotels found`,
      description: `${q.city} · ${q.checkIn} → ${q.checkOut} · ${q.rooms} room(s), ${q.guests} guests`,
    });
  };

  const nights = Math.max(
    1,
    Math.round((new Date(q.checkOut) - new Date(q.checkIn)) / (1000 * 60 * 60 * 24))
  );

  const closeDialog = () => {
    setSelected(null);
    setConfirmed(null);
    setGuest({ name: "", phone: "", email: "" });
  };

  const book = (e) => {
    e.preventDefault();
    if (!guest.name || !guest.phone) {
      toast({ title: "Details required", description: "Please enter name & phone." });
      return;
    }
    setConfirmed({ code: "PMRH" + Date.now().toString().slice(-8), data: selected });
  };

  const amenityIcon = {
    WiFi: Wifi,
    Pool: Waves,
    Spa: Sparkles,
    Restaurant: Utensils,
    Breakfast: Utensils,
    Meals: Utensils,
    Bar: Utensils,
    Beach: Waves,
    Backwater: Waves,
    Ayurveda: Sparkles,
    Heritage: Sparkles,
    "Taj View": Sparkles,
    "Mountain View": Sparkles,
    "Boat Ride": Waves,
    Heater: Sparkles,
    Parking: Sparkles,
  };

  return (
    <section id="hotels" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold tracking-wider uppercase">
            <Hotel className="w-3.5 h-3.5" /> Hotel Booking
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Stay in style, anywhere
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Handpicked properties from budget stays to 5-star luxury — with the best-price guarantee & free cancellation on select rooms.
          </p>
        </div>

        <form
          onSubmit={search}
          className="mt-10 bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-3 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-2 items-center"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
            <MapPin className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">City / Destination</label>
              <Input
                value={q.city}
                onChange={(e) => setQ({ ...q, city: e.target.value })}
                placeholder="Goa, Manali, Udaipur..."
                list="popular-cities"
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
              />
              <datalist id="popular-cities">
                {POPULAR_HOTEL_CITIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Calendar className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Check-in</label>
              <Input
                type="date"
                value={q.checkIn}
                onChange={(e) => setQ({ ...q, checkIn: e.target.value })}
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Calendar className="w-5 h-5 text-orange-500" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Check-out</label>
              <Input
                type="date"
                value={q.checkOut}
                onChange={(e) => setQ({ ...q, checkOut: e.target.value })}
                className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
            <Users className="w-5 h-5 text-teal-600" />
            <div className="flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Rooms & Guests</label>
              <div className="flex items-center gap-2 h-6">
                <Input
                  type="number"
                  min="1"
                  max="9"
                  value={q.rooms}
                  onChange={(e) => setQ({ ...q, rooms: e.target.value })}
                  className="border-0 p-0 h-6 w-6 focus-visible:ring-0 shadow-none text-slate-900"
                />
                <span className="text-xs text-slate-500">rm</span>
                <span className="text-slate-300">·</span>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={q.guests}
                  onChange={(e) => setQ({ ...q, guests: e.target.value })}
                  className="border-0 p-0 h-6 w-6 focus-visible:ring-0 shadow-none text-slate-900"
                />
                <span className="text-xs text-slate-500">gst</span>
              </div>
            </div>
          </div>
          <Button type="submit" className="h-14 md:h-auto md:aspect-square md:w-14 bg-teal-600 hover:bg-teal-700 rounded-xl">
            <Search className="w-5 h-5" />
            <span className="md:hidden ml-2">Search Hotels</span>
          </Button>
        </form>

        <div className="mt-10">
          <div className="flex items-end justify-between mb-5">
            <h3 className="text-xl font-bold text-slate-900">Featured Hotels</h3>
            <span className="text-sm text-slate-500">{results.length} results · {nights} night{nights > 1 ? "s" : ""}</span>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {results.map((h) => (
              <div
                key={h.id}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-teal-500 hover:shadow-2xl hover:-translate-y-1 transition duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={h.image}
                    alt={h.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className="bg-white/95 text-slate-800 hover:bg-white gap-1">
                      {[...Array(h.stars)].map((_, i) => (
                        <Star key={i} className="w-3 h-3 fill-orange-500 text-orange-500" />
                      ))}
                    </Badge>
                    <Badge className="bg-teal-600 text-white hover:bg-teal-600">{h.tag}</Badge>
                  </div>
                  <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-bold text-slate-900">
                    <Star className="w-3 h-3 fill-orange-500 text-orange-500" />
                    {h.rating}
                    <span className="text-slate-500 font-medium">({h.reviews})</span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg leading-snug">{h.name}</h4>
                      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                        <MapPin className="w-3 h-3" /> {h.area}, {h.city}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1">
                    {h.amenities.slice(0, 4).map((a) => {
                      const Icon = amenityIcon[a] || Sparkles;
                      return (
                        <span
                          key={a}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-teal-50 text-teal-700 text-[10px] font-medium"
                        >
                          <Icon className="w-2.5 h-2.5" /> {a}
                        </span>
                      );
                    })}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-end justify-between">
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-slate-900">₹{h.price.toLocaleString("en-IN")}</span>
                        <span className="text-xs text-slate-400 line-through">₹{h.original.toLocaleString("en-IN")}</span>
                      </div>
                      <div className="text-[10px] text-slate-500">per room, per night</div>
                    </div>
                    <Button
                      onClick={() => setSelected(h)}
                      className="bg-orange-500 hover:bg-orange-600 text-white h-9"
                    >
                      Book <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg">
          {selected && !confirmed && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Book {selected.name}
                </DialogTitle>
                <DialogDescription>
                  {selected.area}, {selected.city} · {q.checkIn} → {q.checkOut} · {nights} night{nights > 1 ? "s" : ""}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={book} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="hname" className="text-xs">Guest Name *</Label>
                  <Input id="hname" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="hphone" className="text-xs">Phone *</Label>
                    <Input id="hphone" value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                  <div>
                    <Label htmlFor="hemail" className="text-xs">Email</Label>
                    <Input id="hemail" type="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} placeholder="you@example.com" />
                  </div>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Room rate ({nights} × ₹{selected.price.toLocaleString("en-IN")})</span><span className="font-semibold">₹{(nights * selected.price).toLocaleString("en-IN")}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Taxes & fees (18%)</span><span className="font-semibold">₹{Math.round(nights * selected.price * 0.18).toLocaleString("en-IN")}</span></div>
                </div>
                <div className="pt-2 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Total</div>
                    <div className="text-2xl font-bold text-slate-900">₹{Math.round(nights * selected.price * 1.18).toLocaleString("en-IN")}</div>
                  </div>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Confirm & Pay</Button>
                </div>
              </form>
            </>
          )}

          {confirmed && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <BedDouble className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Hotel Booked!
              </h3>
              <p className="mt-2 text-slate-600 text-sm">
                Confirmation voucher has been sent to your email & phone.
              </p>
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono font-semibold">{confirmed.code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Hotel</span><span className="font-semibold">{confirmed.data.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Check-in</span><span className="font-semibold">{q.checkIn}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Check-out</span><span className="font-semibold">{q.checkOut}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Guest</span><span className="font-semibold">{guest.name}</span></div>
              </div>
              <Button onClick={closeDialog} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Hotel booking is MOCKED (no real transaction).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default HotelSearch;
